import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usePullRequests } from '@/features/pull-requests/hooks/usePullRequests';
import { api } from '@/lib/api';

export type QuickstartStepId =
  | 'connect_issue_tracker'
  | 'connect_scm'
  | 'start_planning'
  | 'approve_artifacts'
  | 'connect_mcp'
  | 'implement_context'
  | 'open_pr_review'
  | 'explore_inspect';

export interface QuickstartStepProgress {
  id: QuickstartStepId;
  complete: boolean;
}

interface QuickstartWorkItemRow {
  prCount?: number;
  artifactApprovalSummary?: {
    specStatus: 'approved' | 'pending' | 'changes_requested' | null;
    techPlanStatus: 'approved' | 'pending' | 'changes_requested' | null;
    tasksStatus: 'approved' | 'pending' | 'changes_requested' | null;
    testsStatus: 'approved' | 'pending' | 'changes_requested' | null;
  } | null;
}

interface QuickstartMcpGrantRow {
  status: string;
}

function hasAllArtifactApprovals(item: QuickstartWorkItemRow): boolean {
  const summary = item.artifactApprovalSummary;
  if (!summary) {
    return false;
  }

  return (
    summary.specStatus === 'approved' &&
    summary.techPlanStatus === 'approved' &&
    summary.testsStatus === 'approved' &&
    summary.tasksStatus === 'approved'
  );
}

const QUICKSTART_INSPECT_VISITED_KEY = 'codemerlin-quickstart-inspect-visited';

export function quickstartInspectVisitedStorageKey(organizationId: string, userId: string): string {
  return `${organizationId}:${userId}`;
}

export function readQuickstartInspectVisited(
  organizationId: string | undefined,
  userId: string | undefined
): boolean {
  if (!organizationId || !userId || typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(QUICKSTART_INSPECT_VISITED_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as Record<string, boolean>;
    return parsed[quickstartInspectVisitedStorageKey(organizationId, userId)] === true;
  } catch {
    return false;
  }
}

export function writeQuickstartInspectVisited(
  organizationId: string,
  userId: string,
  visited: boolean
): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(QUICKSTART_INSPECT_VISITED_KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
    parsed[quickstartInspectVisitedStorageKey(organizationId, userId)] = visited;
    window.localStorage.setItem(QUICKSTART_INSPECT_VISITED_KEY, JSON.stringify(parsed));
  } catch {
    // ignore storage failures
  }
}

const QUICKSTART_DISMISS_KEY = 'codemerlin-quickstart-dismissed';
const QUICKSTART_SESSION_AUTO_OPENED_KEY = 'codemerlin-quickstart-session-auto-opened';

export function quickstartDismissStorageKey(organizationId: string, userId: string): string {
  return `${organizationId}:${userId}`;
}

export const QUICKSTART_OPEN_EVENT = 'codemerlin:open-quickstart';

export function readQuickstartDismissed(
  organizationId: string | undefined,
  userId: string | undefined
): boolean {
  if (!organizationId || !userId || typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(QUICKSTART_DISMISS_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as Record<string, boolean>;
    return parsed[quickstartDismissStorageKey(organizationId, userId)] === true;
  } catch {
    return false;
  }
}

export function writeQuickstartDismissed(
  organizationId: string,
  userId: string,
  dismissed: boolean
): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(QUICKSTART_DISMISS_KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
    parsed[quickstartDismissStorageKey(organizationId, userId)] = dismissed;
    window.localStorage.setItem(QUICKSTART_DISMISS_KEY, JSON.stringify(parsed));
  } catch {
    // ignore storage failures
  }
}

export function readQuickstartSessionAutoOpened(
  organizationId: string | undefined,
  userId: string | undefined
): boolean {
  if (!organizationId || !userId || typeof window === 'undefined') return false;
  try {
    const raw = window.sessionStorage.getItem(QUICKSTART_SESSION_AUTO_OPENED_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as Record<string, boolean>;
    return parsed[quickstartDismissStorageKey(organizationId, userId)] === true;
  } catch {
    return false;
  }
}

export function writeQuickstartSessionAutoOpened(
  organizationId: string,
  userId: string,
  opened: boolean
): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.sessionStorage.getItem(QUICKSTART_SESSION_AUTO_OPENED_KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
    parsed[quickstartDismissStorageKey(organizationId, userId)] = opened;
    window.sessionStorage.setItem(QUICKSTART_SESSION_AUTO_OPENED_KEY, JSON.stringify(parsed));
  } catch {
    // ignore storage failures
  }
}

interface QuickstartWorkItemSettings {
  jira?: { isConnected?: boolean };
  linear?: { isConnected?: boolean };
  /** Azure Boards (spec 493): an enabled row counts as a tracker. */
  azureBoards?: unknown;
}

interface QuickstartScmConnection {
  isActive?: boolean;
}

export function useQuickstartProgress(
  organizationId: string | undefined,
  userId: string | undefined,
  enabled: boolean
) {
  const queryEnabled = enabled && !!organizationId;

  const { data: workItemSettings, isLoading: workItemSettingsLoading } = useQuery({
    queryKey: ['work-item-settings', organizationId],
    queryFn: () =>
      api.get<QuickstartWorkItemSettings>(`organizations/${organizationId}/work-item-settings`),
    enabled: queryEnabled,
    staleTime: 60_000,
  });

  const { data: scmConnections = [], isLoading: scmConnectionsLoading } = useQuery({
    queryKey: ['scm-connections', organizationId],
    queryFn: () =>
      api.get<QuickstartScmConnection[]>(`/organizations/${organizationId}/scm-connections`),
    enabled: queryEnabled,
    staleTime: 60_000,
  });

  const { data: workItems = [], isLoading: workItemsListLoading } = useQuery({
    queryKey: ['quickstart-work-items', organizationId],
    queryFn: async () => {
      const params = new URLSearchParams({
        sort: 'updated_desc',
        limit: '20',
        offset: '0',
      });
      return api.get<QuickstartWorkItemRow[]>(
        `organizations/${organizationId}/work-items?${params.toString()}`
      );
    },
    enabled: queryEnabled,
    staleTime: 60_000,
  });
  // `usePullRequests` has no `enabled` option of its own and polls every 10s,
  // so starving the org id is the only way to switch it off with the rest of
  // the progress queries. When enabled the query key is unchanged.
  const { data: pullRequests, isLoading: pullRequestsLoading } = usePullRequests(
    queryEnabled ? organizationId : undefined,
    { limit: 20 }
  );
  const { data: mcpGrants = [], isLoading: mcpGrantsLoading } = useQuery({
    queryKey: ['quickstart-mcp-grants'],
    queryFn: async () => {
      const data = await api.get<{ grants: QuickstartMcpGrantRow[] }>('/users/me/mcp/grants');
      return data.grants ?? [];
    },
    enabled: queryEnabled,
    staleTime: 60_000,
  });

  const steps = useMemo<QuickstartStepProgress[]>(() => {
    const hasIssueTracker =
      workItemSettings?.jira?.isConnected === true ||
      workItemSettings?.linear?.isConnected === true ||
      (workItemSettings?.azureBoards !== undefined && workItemSettings.azureBoards !== null);
    const hasScmConnection = scmConnections.some((connection) => connection.isActive !== false);
    const hasWorkItem = workItems.length > 0;
    const hasAllApprovals = workItems.some(hasAllArtifactApprovals);
    const hasMcpGrant = mcpGrants.some((grant) => grant.status === 'active');
    const prs = pullRequests?.data ?? [];
    const hasCompletedReview = prs.some((pr) => pr.latestReview?.status === 'completed');
    const hasVisitedInspect = readQuickstartInspectVisited(organizationId, userId);

    return [
      { id: 'connect_issue_tracker', complete: hasIssueTracker },
      { id: 'connect_scm', complete: hasScmConnection },
      { id: 'start_planning', complete: hasWorkItem },
      { id: 'approve_artifacts', complete: hasAllApprovals },
      { id: 'connect_mcp', complete: hasMcpGrant },
      {
        id: 'implement_context',
        complete: hasAllApprovals && hasMcpGrant,
      },
      { id: 'open_pr_review', complete: hasCompletedReview },
      {
        id: 'explore_inspect',
        complete: hasVisitedInspect || hasCompletedReview,
      },
    ];
  }, [
    workItemSettings,
    scmConnections,
    workItems,
    pullRequests,
    mcpGrants,
    organizationId,
    userId,
  ]);

  const completedCount = steps.filter((s) => s.complete).length;
  const allComplete = completedCount === steps.length;
  const isLoading =
    queryEnabled &&
    (workItemSettingsLoading ||
      scmConnectionsLoading ||
      workItemsListLoading ||
      pullRequestsLoading ||
      mcpGrantsLoading);

  return {
    steps,
    completedCount,
    totalCount: steps.length,
    allComplete,
    isLoading,
  };
}
